import { BasePack, BasePackBeingCreated } from 'types/pack';
import { Box, Checkbox, FormControl, InputAdornment, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';

import ImageField from './ImageField';
import StoreContext from 'contexts/storeContext';
import { makeStyles } from 'tss-react/mui';
import { useContext } from 'react';
import {
  getSurprisePackDescriptionHelperText,
  getSurprisePackNameHelperText,
  multilineHelperTextProps,
  SURPRISE_NAME_PREFIX_LABEL,
} from 'surprisePack';

const useStyles = makeStyles()((theme) => ({
  field: {
    paddingBottom: theme.spacing(2),
  },
  image: {
    height: theme.spacing(8),
    display: 'block',
    margin: theme.spacing(1),
  },
}));

type Props = {
  pack: BasePackBeingCreated;
  packToEdit?: BasePack;
  isOpen: boolean;
  handleChange: (event: any) => void;
  getFieldError: (field: string) => string | undefined;
  onClose: () => void;
  isSurprise?: boolean;
  orgImagesOnly?: boolean;
  disabled?: boolean;
};

export default function BasePackDialogFields({
  pack,
  packToEdit,
  isOpen,
  handleChange,
  getFieldError,
  onClose,
  isSurprise,
  orgImagesOnly,
  disabled,
}: Props) {
  const { tags, store } = useContext(StoreContext);
  const { classes } = useStyles();
  const minimumDiscount = isSurprise ? (store?.surprise_minimum_discount ?? store?.minimum_discount) : store?.minimum_discount;

  return (
    <>
      <FormControl className={classes.field} required fullWidth>
        <TextField
          margin='dense'
          id='name'
          name='name'
          label='Nombre'
          value={pack['name']}
          error={!!getFieldError('name')}
          helperText={getFieldError('name') || (isSurprise ? getSurprisePackNameHelperText() : '')}
          InputProps={{
            startAdornment: isSurprise ? (
              <InputAdornment position='start'>{SURPRISE_NAME_PREFIX_LABEL}</InputAdornment>
            ) : undefined,
          }}
          onChange={handleChange}
          type='text'
          required
          disabled={disabled}
        />
      </FormControl>
      <FormControl className={classes.field} required fullWidth>
        <TextField
          margin='dense'
          id='description'
          name='description'
          label='Descripción'
          value={pack['description']}
          error={!!getFieldError('description')}
          helperText={getFieldError('description') || (isSurprise ? getSurprisePackDescriptionHelperText() : '')}
          FormHelperTextProps={isSurprise && !getFieldError('description') ? multilineHelperTextProps : undefined}
          onChange={handleChange}
          type='text'
          required
          disabled={disabled}
        />
      </FormControl>
      <FormControl className={classes.field} required fullWidth>
        <TextField
          margin='dense'
          id='original_price'
          name='original_price'
          label='Precio original'
          value={pack['original_price']}
          error={!!getFieldError('original_price')}
          helperText={getFieldError('original_price') || 'Precio habitual de este producto en la tienda, sin descuento.'}
          onChange={handleChange}
          type='number'
          required
          disabled={disabled}
        />
      </FormControl>
      {minimumDiscount !== undefined && minimumDiscount !== null && isSurprise && (
        <Box className={classes.field}>
          <Typography variant='body2' sx={{ fontWeight: 700, color: 'success.main' }}>
            Descuento mínimo para packs {SURPRISE_NAME_PREFIX_LABEL}: {minimumDiscount}%.
          </Typography>
        </Box>
      )}
      <FormControl className={classes.field} required fullWidth>
        <TextField
          margin='dense'
          id='price'
          name='price'
          label='Precio'
          value={pack['price']}
          error={!!getFieldError('price')}
          helperText={getFieldError('price') || 'Precio de venta en la app.'}
          onChange={handleChange}
          type='number'
          required
          disabled={disabled}
        />
      </FormControl>
      <FormControl className={classes.field} key='tags' fullWidth>
        <InputLabel>Tags</InputLabel>
        <Select
          name='tags'
          multiple
          value={pack['tags'] || []}
          renderValue={(selected) =>
            !!tags &&
            tags
              .filter((i) => selected.includes(i.id))
              .map((i) => i.name)
              .join(', ')
          }
          onChange={handleChange}
          label='Tags'
          disabled={disabled}
        >
          {!!tags &&
            tags.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                <Checkbox color='primary' checked={pack['tags']?.includes(option.id)} />
                {option.name}
              </MenuItem>
            ))}
        </Select>
      </FormControl>
      <FormControl className={classes.field} required fullWidth>
        <ImageField
          isOpen={isOpen}
          initialImageId={packToEdit?.image?.id}
          onClose={onClose}
          pack={pack}
          handleChange={handleChange}
          error={getFieldError('image')}
          orgImagesOnly={orgImagesOnly}
          disabled={disabled}
        />
      </FormControl>
    </>
  );
}
